import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { avatarBg, getAvatarUrl, getInitials } from "~/utils/avatar";
import { trpc } from "~/utils/trpc";
import { useAuth } from "../AuthProvider";
import { CenterModal } from "../CenterModal";
import {
  MODAL_FORM_LAYOUT,
  modalFormFieldLabelSx,
  modalFormIntroBodySx,
  modalFormMultilineTextFieldSx,
  modalFormSelectSx,
  modalFormTextFieldSx,
} from "../modalFormStyles";
import { useTeamProject } from "../TeamProjectProvider";
import { useToast } from "../ToastProvider";
import TelegramGuideModal from "../TelegramGuideModal";

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  onCreated?: (projectId: string) => void;
}

export default function CreateProjectModal({
  open,
  onClose,
  teamId,
  onCreated,
}: CreateProjectModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { showActionToast } = useToast();
  const { authData } = useAuth();
  const utils = trpc.useUtils();

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [assigneeError, setAssigneeError] = useState("");
  const [channelId, setChannelId] = useState("");
  const [channelIdError, setChannelIdError] = useState("");
  const [channelError, setChannelError] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const [showTelegramGuide, setShowTelegramGuide] = useState(false);

  const { data: membersData } = trpc.team.members.useQuery(
    { teamId },
    { enabled: open },
  );
  const members = membersData?.members;

  // Pre-select the current user once members load. Only auto-fills the first
  // time it sees a populated list per modal-open so a user who clears the
  // field stays in the empty state.
  useEffect(() => {
    if (!open || !members || assigneeId) return;
    const me = members.find((m) => m.user?.id === authData?.userId);
    if (me) setAssigneeId(me.id);
  }, [open, members, authData?.userId, assigneeId]);

  const { projects } = useTeamProject();
  const { data: archivedProjects } = trpc.project.listArchived.useQuery(
    { teamId },
    { enabled: open },
  );
  const usedChannelIds = new Set(
    [...projects, ...(archivedProjects ?? [])]
      .filter((p) => p.messengerChannelId)
      .map((p) => p.messengerChannelId),
  );

  // Check if the team has a messenger server linked
  const { data: teams } = trpc.team.list.useQuery(undefined, { enabled: open });
  const currentTeam = teams?.find((t) => t.id === teamId);
  const hasServer = Boolean(currentTeam?.messengerServerId);
  const isSlack = currentTeam?.platformType === "slack";
  const isTelegram = currentTeam?.platformType === "telegram";

  // Fetch messenger channels only when team has a server linked
  const { data: messengerChannels, isLoading: channelsLoading } =
    trpc.project.messengerChannels.useQuery(
      { teamId },
      { enabled: open && hasServer },
    );

  const linkChannelMutation = trpc.project.linkChannel.useMutation();

  const createMutation = trpc.project.create.useMutation({
    onSuccess: async (project) => {
      // Slack/Discord: link the chosen channel to the new project. Telegram
      // skips this — the bot binds the chat to this project later via the
      // single-active-project branch in services/telegram/auto-link.ts.
      if (!isTelegram && channelId) {
        const selectedChannel = messengerChannels?.find(
          (c) => c.id === channelId,
        );
        const linkPlatform =
          currentTeam?.platformType === "slack" ? "slack" : "discord";
        await linkChannelMutation.mutateAsync({
          projectId: project.id,
          messengerChannelId: channelId,
          messengerChannelName: selectedChannel?.name ?? channelId,
          platformType: linkPlatform,
        });
      }
      showActionToast({ title: t("project.createSuccess"), description: t("project.createSuccessDesc", { name: project.name }), severity: "success" });
      utils.project.list.invalidate({ teamId });
      onCreated?.(project.id);
      if (isTelegram) {
        setShowTelegramGuide(true);
      } else {
        handleClose();
      }
    },
    onError: () => showActionToast({ title: t("project.createFailed"), severity: "error" }),
  });

  const handleSubmit = () => {
    if (!name.trim()) {
      setNameError(t("project.nameRequired"));
      return;
    }
    if (!assigneeId) {
      setAssigneeError(t("project.assigneeRequired"));
      return;
    }
    // Telegram projects skip the channel field; the bot binds the chat when
    // the user later adds it to a group via the TelegramGuideModal flow.
    if (!isTelegram) {
      if (!channelId.trim()) {
        setChannelError(t("project.channelRequired"));
        return;
      }
      if (!hasServer) {
        const trimmed = channelId.trim();
        if (isSlack && !/^[A-Z0-9]{9,12}$/i.test(trimmed)) {
          setChannelIdError(t("slack.invalidChannelId"));
          return;
        }
        if (!isSlack && !/^\d{17,20}$/.test(trimmed)) {
          setChannelIdError(t("discord.invalidChannelId"));
          return;
        }
      }
      if (usedChannelIds.has(channelId)) {
        setChannelError(t("project.channelAlreadyLinked"));
        return;
      }
    }
    setNameError("");
    setAssigneeError("");
    setChannelIdError("");
    setChannelError("");
    createMutation.mutate({
      teamId,
      name: name.trim(),
      description: description.trim() || undefined,
      assigneeId,
    });
  };

  const handleClose = () => {
    setName("");
    setNameError("");
    setDescription("");
    setAssigneeId("");
    setAssigneeError("");
    setChannelId("");
    setChannelIdError("");
    setChannelError("");
    setShowGuide(false);
    setShowTelegramGuide(false);
    onClose();
  };

  const isPending = createMutation.isPending || linkChannelMutation.isPending;

  const labelSx = modalFormFieldLabelSx(theme);
  const inputSx = modalFormTextFieldSx(theme);
  const multilineSx = modalFormMultilineTextFieldSx(theme);
  const selectSx = modalFormSelectSx(theme);

  // After a Telegram create, swap to TelegramGuideModal so the user adds
  // the bot — the bot then binds the chat to this fresh project via the
  // single-active-project branch in services/telegram/auto-link.ts. Hooks
  // above run unconditionally to keep Rules-of-Hooks ordering stable.
  if (isTelegram && showTelegramGuide) {
    return (
      <TelegramGuideModal open={open} onClose={handleClose} teamId={teamId} />
    );
  }

  return (
    <CenterModal
      open={open}
      onClose={handleClose}
      title={t("project.create")}
      size="md"
      width={480}
      hideCloseButton
    >
      <Box>
        <Stack spacing={MODAL_FORM_LAYOUT.fieldStackGap}>
          {/* Intro mentions channel mapping — irrelevant for Telegram, where
              the chat is bound later via the bot. */}
          {!isTelegram && (
            <Typography variant="body1" sx={modalFormIntroBodySx(theme)}>
              {t("project.channelDescription")}
            </Typography>
          )}

          {/* Project name */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.name")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t("project.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError("");
              }}
              error={Boolean(nameError)}
              helperText={nameError}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
              sx={inputSx}
            />
          </Box>

          {/* Description */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.description")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t("project.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              sx={multilineSx}
            />
          </Box>

          {/* Assignee */}
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.assignee")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={assigneeId}
              onChange={(e) => {
                setAssigneeId(e.target.value);
                setAssigneeError("");
              }}
              error={Boolean(assigneeError)}
              aria-label={t("project.assignee")}
              sx={selectSx}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.custom.gray[500] }}
                    >
                      {t("project.assigneeRequired")}
                    </Typography>
                  );
                }
                const m = members?.find((m) => m.id === selected);
                const name = m?.messengerNickname ?? m?.user?.name ?? "";
                return (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={m?.user ? getAvatarUrl(m.user) : undefined}
                      sx={{
                        width: 20,
                        height: 20,
                        fontSize: 9,
                        fontWeight: 700,
                        bgcolor: avatarBg(name),
                      }}
                    >
                      {getInitials(name)}
                    </Avatar>
                    <Typography variant="body2">{name}</Typography>
                  </Stack>
                );
              }}
            >
              {members?.map((m) => {
                const name = m.messengerNickname ?? m.user?.name ?? "";
                return (
                  <MenuItem key={m.id} value={m.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        src={m.user ? getAvatarUrl(m.user) : undefined}
                        sx={{
                          width: 20,
                          height: 20,
                          fontSize: 9,
                          fontWeight: 700,
                          bgcolor: avatarBg(name),
                        }}
                      >
                        {getInitials(name)}
                      </Avatar>
                      <Typography variant="body2">{name}</Typography>
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
            {assigneeError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                {assigneeError}
              </Typography>
            )}
          </Box>

          {/* Channel section — hidden for Telegram; the bot binds the chat
              to this project later via TelegramGuideModal + my_chat_member. */}
          {!isTelegram && (
          <Box>
            <Typography component="label" variant="body2" sx={labelSx}>
              {t("project.channel")}
              <Box
                component="span"
                sx={{ color: theme.palette.custom.destructive, ml: 0.25 }}
              >
                *
              </Box>
            </Typography>

            {hasServer ? (
              /* Channel dropdown when server is linked */
              <Box>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={channelId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setChannelId(id);
                    setChannelError("");
                    const ch = messengerChannels?.find((c) => c.id === id);
                    if (ch && !name.trim()) setName(ch.name);
                  }}
                  disabled={channelsLoading}
                  error={Boolean(channelError)}
                  sx={selectSx}
                >
                <MenuItem value="">
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.custom.gray[500] }}
                  >
                    {channelsLoading
                      ? t("common.loading")
                      : t("common.selectChannel")}
                  </Typography>
                </MenuItem>
                {messengerChannels?.map((ch) => {
                  const isUsed = usedChannelIds.has(ch.id);
                  return (
                    <MenuItem key={ch.id} value={ch.id} disabled={isUsed}>
                      # {ch.name}{" "}
                      {isUsed ? t("project.channelAlreadyLinked") : ""}
                    </MenuItem>
                  );
                })}
              </Select>
              {channelError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {channelError}
                </Typography>
              )}
            </Box>
          ) : (
            /* Manual input when no server linked */
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder={
                  isSlack
                    ? t("slack.channelIdPlaceholder")
                    : t("discord.channelIdPlaceholder")
                }
                value={channelId}
                onChange={(e) => {
                  setChannelId(e.target.value);
                  setChannelIdError("");
                  setChannelError("");
                }}
                error={Boolean(channelIdError || channelError)}
                helperText={
                  channelIdError || channelError || t("project.channelIdHelper")
                }
                sx={inputSx}
              />

              <Box
                mt={1}
                sx={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: theme.palette.custom.gray[500],
                  "&:hover": { color: theme.palette.custom.primary },
                }}
                onClick={() => setShowGuide((v) => !v)}
              >
                {showGuide ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
                <Typography variant="caption" fontWeight={500}>
                  {t(isSlack ? "slack.howToFind" : "discord.howToFind")}
                </Typography>
              </Box>
              <Collapse in={showGuide}>
                <Box
                  mt={1}
                  p={1.5}
                  sx={{
                    bgcolor: theme.palette.custom.gray[100],
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: theme.palette.custom.divider,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-line", display: "block" }}
                  >
                    {t(
                      isSlack
                        ? "slack.howToFindGuide"
                        : "discord.howToFindGuide",
                    )}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
          )}
        </Stack>

        {/* Actions */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{
            mt: MODAL_FORM_LAYOUT.gapFieldSectionToActions,
            pb: MODAL_FORM_LAYOUT.actionsPaddingBottom,
          }}
        >
          <Button
            variant="tertiary"
            onClick={handleClose}
            fullWidth
            sx={{
              fontWeight: 600,
              px: 2,
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !name.trim() ||
              !assigneeId ||
              (!isTelegram && !channelId) ||
              isPending
            }
            fullWidth
            sx={{
              fontWeight: 600,
              px: 3,
              "&.Mui-disabled": {
                bgcolor: theme.palette.custom.primaryDisabled,
                color: theme.palette.custom.gray[0],
              },
            }}
          >
            {t("project.createButton")}
          </Button>
        </Stack>
      </Box>
    </CenterModal>
  );
}
